<template>
  <ValidationObserver ref="prospectFormPage">
    <div class="card card-custom">
      <div class="card-body container">

        <b-card border-variant="primary" class="mb-5 mt-3" body-class="px-7 py-5">
          <b-alert v-if="errorAlert" variant="warning" show>
            <div class="text-dark" v-html="errorAlert"></div>
          </b-alert>

          <div>
            <span class="font-weight-boldest font-size-h5">Proposed Insured Entity</span>
            <div class="form-group row">
              <label class="col-1 col-form-label">Search ABN<span class="text-danger">*</span></label>
              <span class="col-3">
                <ValidationProvider :rules="{ regex: /^[\d\s]+$/ }" name="abnSearchString" v-slot="{ errors }">
                  <input type="text" class="form-control" v-model.trim="abnSearchString" maxlength="15"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </span>
              <button type="button" class="btn btn-primary" @click.prevent="searchAbn"
                      :disabled="!abnSearchString || searchingAbn">Search</button>
              <b-spinner v-if="searchingAbn" variant="primary" class="ml-3 mt-1"></b-spinner>
            </div>
            <div class="form-group" v-if="abnSearchError">
              <label class="text-danger">{{ abnSearchError }}</label>
            </div>

            <div v-if="prospectInfo && prospectInfo.entityName && prospectInfo.abn" class="mb-5 mt-5">
              <div class="form-group row mb-0">
                <div class="col-1">Entity Name</div>
                <div class="col-5 font-weight-boldest font-size-h6">{{ prospectInfo.entityName }}</div>
              </div>
              <div class="form-group row mb-0">
                <div class="col-1">ABN</div>
                <div class="col-5">{{ prospectInfo.abn }}</div>
              </div>
              <div v-if="prospectInfo.acn" class="form-group row mb-0">
                <div class="col-1">ACN</div>
                <div class="col-5">{{ prospectInfo.acn }}</div>
              </div>
            </div>

            <b-spinner v-if="searchingDuplicateClients" variant="primary" class="mt-1"></b-spinner>
            <div v-if="!searchingDuplicateClients && Array.isArray(duplicateClients) && duplicateClients.length > 0" class="mb-5">
              <label class="col-form-label text-danger">{{duplicateClients.length}} client(s)/prospect(s) with the same ABN already exist in the CRM.</label>
              <table class="table table-hover table-sm table-bordered">
                <thead>
                <th width="30%" class="text-center">Client Name</th>
                <th width="30%" class="text-center">Trading Name</th>
                <th width="5%" class="text-center">Brand</th>
                <th width="10%" class="text-center">iBais Code</th>
                <th width="5%" class="text-center">Status</th>
                <th width="10%" class="text-center">CRM Record</th>
                <th width="10%" class="text-center">Box Folder</th>
                </thead>
                <tbody>
                <tr v-for="(dupClient, index) in duplicateClients" :key="index">
                  <td class="align-middle">
                    {{ dupClient.Name }}
                  </td>
                  <td class="align-middle">
                    {{ dupClient.K_TradingName }}
                  </td>
                  <td class="text-center align-middle">
                    <strong>{{ dupClient.K_Database }}</strong>
                  </td>
                  <td class="text-center align-middle">
                    <strong>{{ dupClient.K_IbaisClientNumber }}</strong>
                  </td>
                  <td class="text-center align-middle">
                    {{ dupClient.K_Status }}
                  </td>
                  <td class="text-center">
                    <a target="_blank" :href="`https://crm.zoho.com.au/crm/tab/CustomModule1/${dupClient.id}`">
                      <i class="fas fa-external-link-alt fa-lg"></i>
                    </a>
                  </td>
                  <td class="text-center">
                      <a v-if="dupClient.K_ClientBoxFolderId" target="_blank" :href="`https://dkg.app.box.com/folder/${dupClient.K_ClientBoxFolderId}`">
                        <i class="fas fa-external-link-alt fa-lg"></i>
                      </a>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div v-if="prospectInfo && !searchingDuplicateClients">
              <label class="col-form-label font-size-h6 font-weight-boldest">Enter Prospect Information</label>

              <div class="form-group row mb-2">
                <label class="col-2 col-form-label text-right">Brand<span class="text-danger">*</span></label>
                <div class="col-2">
                  <ValidationProvider rules="required" name="brand" v-slot="{ errors }">
                    <b-form-select class="custom-select custom-select-md" v-model="prospectInfo.brand">
                      <option v-for="(brand, index) in availableBrands" :key="index" :value="brand">{{ brand }}</option>
                    </b-form-select>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="form-group row mb-2">
                <label class="col-2 col-form-label text-right">iBais Client Number<span class="text-danger">*</span></label>
                <div class="col-2">
                  <ValidationProvider :rules="{ required: true, regex: /^[a-zA-Z0-9]*$/, max: 255 }" name="ibaisClientCode" v-slot="{ errors }">
                    <input type="text" class="form-control text-uppercase" v-model.trim="prospectInfo.ibaisClientCode" maxlength="255"/>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="form-group row mb-2">
                <label class="col-2 col-form-label text-right">Trading Name</label>
                <div class="col-4">
                  <ValidationProvider rules="max:255" name="tradingName" v-slot="{ errors }">
                    <input type="text" class="form-control" v-model.trim="prospectInfo.tradingName" maxlength="255"/>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="d-flex justify-content-between mt-10">
                <div class="mr-2">
                  <button class="btn btn-primary font-weight-bold text-uppercase px-9 py-4"
                          :disabled="!isProspectValid" @click.prevent="createProspect">
                    Create Prospect
                  </button>
                </div>
              </div>
            </div>

          </div>
        </b-card>
      </div>
    </div>
  </ValidationObserver>
</template>

<style lang="scss">

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>

<script>
import {ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME} from "@/core/services/store/htmlclass.module";
import _ from "lodash";
import KTUtil from "@/assets/js/components/util";
import ApiService from "@/core/services/api.service";

export default {
  name: "ProspectForm",

  components: {},

  data() {
    return {
      errorAlert: null,
      prospectInfo: null,
      abnSearchString: null,
      abnSearchError: null,
      searchingAbn: false,
      duplicateClients: null,
      searchingDuplicateClients: false,
    };
  },

  mounted() {
    this.startPageLoading();

    this.$store.commit("setWizardTitle", "Prospect Onboarding Form");

    this.stopPageLoading();
  },

  computed: {
    availableBrands: function () {
      const brands = ['Fuse', 'CRI', 'Rentsure'];
      if(Array.isArray(this.duplicateClients) && this.duplicateClients.length > 0) {
        const usedBrands = _.map(this.duplicateClients, 'K_Database');
        _.remove(brands, brand => {
          return usedBrands.includes(brand);
        });
      }
      return brands;
    },

    isProspectValid: function () {
      if(this.prospectInfo) {
        return this.prospectInfo.entityName && this.prospectInfo.abn && this.prospectInfo.brand && this.prospectInfo.ibaisClientCode;
      }
      return false;
    }
  },

  methods: {

    searchAbn: function() {
      if(!this.abnSearchString) {
        return;
      }
      this.prospectInfo = null;
      this.duplicateClients = null;
      this.searchingDuplicateClients = false;
      this.abnSearchError = null;

      this.searchingAbn = true;
      const searchString = this.abnSearchString.replace(/\s/g, '');
      ApiService.get(`/prospect-form/abn-search`,
          { params: {searchString: searchString} }
      )
          .then(({ data }) => {
            const entity = data.result;

            if(!entity || !entity.abn || !entity.entityName) {
              this.abnSearchError = 'No entity found for the given ABN';
            }
            else if(entity.abnStatus !== 'Active') {
              this.abnSearchError = 'This ABN is not in active status';
            }
            else {
              this.prospectInfo = {};
              this.$set(this.prospectInfo, 'entityName', entity.entityName);
              this.$set(this.prospectInfo, 'abn', entity.abn);
              this.$set(this.prospectInfo, 'acn', entity.acn);
              this.searchDuplicateClients();
            }
            this.searchingAbn = false;
          })
          .catch((error) => {
            this.searchingAbn = false;
            this.abnSearchError = 'Error while searching ABN - please try again';
            if(process.env.NODE_ENV === 'development') {
              console.log(error);
            }
          });
    },

    searchDuplicateClients: function() {
      if(!this.prospectInfo || !this.prospectInfo.abn) {
        return;
      }

      this.duplicateClients = null;
      this.searchingDuplicateClients = true;
      ApiService.get(`/prospect-form/duplicate-clients-search`,
          { params: { abn: this.prospectInfo.abn } }
      )
          .then(({ data }) => {
            this.duplicateClients = data.result;
            this.searchingDuplicateClients = false;
          })
          .catch((error) => {
            this.searchingDuplicateClients = false;
            this.handleError(error);
          });
    },

    createProspect: async function () {
      const isFormValid = await this.$refs.prospectFormPage.validate();
      if(!isFormValid) {
        return;
      }

      this.startPageLoading();
      ApiService.post(`/prospect-form/new-prospect`, this.prospectInfo)
        .then(({ data }) => {
          const newProspect = data.result;
          if(!newProspect) {
            this.stopPageLoading();
            this.errorAlert = 'Error while creating a new prospect! Please try again';
            return;
          }

          // Redirect to the newly created prospect
          window.top.location.href = `https://crm.zoho.com.au/crm/tab/CustomModule1/${newProspect.id}`;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
    },

    handleError: function(errorObj) {
      console.log(errorObj);
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj) {
        let responseStatus = _.get(errorObj, "response.status");
        let errorMessage = errorObj.message || '';
        if (responseStatus === 401 || errorMessage.includes("status code 401") ||
            responseStatus === 403 || errorMessage.includes("status code 403")
        ) {
          errorMsg = "This session is invalid or expired. Please close this window.";
        }
        else {
          let responseResult = _.get(errorObj, "response.data.result");
          if (_.isString(responseResult)) {
            let applicationErrorPrefix = "UI_ERROR:";

            if (responseResult.startsWith(applicationErrorPrefix)) {
              errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
            }
          }
        }
      }
      if (!errorMsg) {
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  },
};
</script>
